import { useEffect, useState, useCallback, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { CircleCheck as CheckCircle2, CircleAlert as AlertCircle, FileText, Loader as Loader2, Mail, PlugZap, RotateCcw, Send, UserRound, Wallet, Zap } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useCompany } from '@/context/CompanyContext';
import { SettingsTabs } from '@/components/settings';
import {
  CREDIT_TERM_OPTIONS,
  DEFAULT_TRANZILA_SETTINGS as DEFAULT_SETTINGS,
  PAYMENT_METHOD_OPTIONS,
  readTranzilaSettings,
  type TranzilaSettings,
} from '@/lib/tranzilaSettings';
import {
  IntegrationSettingsLayout,
  SettingsSection,
  ToggleRow,
  SelectRow,
  NumberRow,
  type IntegrationInfo,
} from './IntegrationSettingsLayout';

const EMAIL_RE = /^[^\s@<>"']+@[^\s@<>"']+\.[^\s@<>"']+$/;
const inputCls = 'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-teal-400';

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="py-3 border-b border-gray-50 last:border-0">
      <label className="block text-sm font-medium text-gray-700 mb-1.5">
        {label}
        <div className="mt-1.5 font-normal">{children}</div>
      </label>
      {hint && <p className="text-xs text-gray-400 mt-1 leading-relaxed">{hint}</p>}
    </div>
  );
}

export function TranzilaIntegrationSettings() {
  const { activeCompanyId } = useCompany();
  const navigate = useNavigate();

  const [integration, setIntegration] = useState<IntegrationInfo | null>(null);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState<string | null>(null);
  const [toast,       setToast]       = useState<string | null>(null);
  const [settings,    setSettings]    = useState<TranzilaSettings>(DEFAULT_SETTINGS);
  const [testing,     setTesting]     = useState(false);
  const [testResult,  setTestResult]  = useState<{ ok: boolean; msg: string } | null>(null);

  const load = useCallback(async () => {
    if (!activeCompanyId) return;
    setLoading(true); setError(null);
    try {
      const { data, error: e } = await supabase
        .from('company_integrations')
        .select('id, provider_name, credentials, settings, is_active, last_sync_at')
        .eq('company_id', activeCompanyId)
        .eq('provider_name', 'tranzila')
        .maybeSingle();
      if (e) throw e;
      if (!data) {
        navigate('/app/settings/integrations', { replace: true });
        return;
      }
      setIntegration(data as IntegrationInfo);
      setSettings(readTranzilaSettings(data.settings));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'שגיאה בטעינת הגדרות');
    } finally {
      setLoading(false);
    }
  }, [activeCompanyId, navigate]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!activeCompanyId || !integration) return;
    const next: TranzilaSettings = {
      ...settings,
      other_payment_description: settings.other_payment_description.trim() || DEFAULT_SETTINGS.other_payment_description,
      fallback_client_name:      settings.fallback_client_name.trim() || DEFAULT_SETTINGS.fallback_client_name,
      email_subject:             settings.email_subject.trim() || DEFAULT_SETTINGS.email_subject,
      email_body:                settings.email_body.trim() || DEFAULT_SETTINGS.email_body,
      email_cc:                  settings.email_cc.trim(),
      bank_code:                 settings.bank_code.replace(/\D/g, ''),
      bank_branch:               settings.bank_branch.replace(/\D/g, ''),
      bank_account:              settings.bank_account.replace(/\D/g, ''),
      cc_installments:           Math.min(36, Math.max(2, Math.round(settings.cc_installments))),
    };
    if (next.email_cc && !EMAIL_RE.test(next.email_cc)) {
      setError('כתובת המייל להעתק אינה תקינה');
      return;
    }
    setSaving(true); setError(null);
    try {
      const merged = { ...integration.settings, ...next };
      const { error: e } = await supabase
        .from('company_integrations')
        .update({ settings: merged })
        .eq('id', integration.id);
      if (e) throw e;
      setSettings(next);
      setIntegration(prev => prev ? { ...prev, settings: merged } : prev);
      setToast('הגדרות שמורות בהצלחה');
      setTimeout(() => setToast(null), 4000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'שגיאה בשמירת הגדרות');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    if (!activeCompanyId) return;
    setTesting(true); setTestResult(null);
    const { data, error: e } = await supabase.functions.invoke('external-invoice', {
      body: { action: 'test_connection', provider: 'tranzila', company_id: activeCompanyId },
    });
    setTesting(false);
    if (data?.ok === true) setTestResult({ ok: true, msg: 'החיבור תקין. טרנזילה זיהתה את שם המסוף ואת המפתחות.' });
    else setTestResult({ ok: false, msg: typeof data?.error === 'string' ? data.error : e ? 'לא הצלחנו להתחבר לשרת. נסו שוב בעוד רגע.' : 'בדיקת החיבור נכשלה.' });
  };

  const set = <K extends keyof TranzilaSettings>(key: K, val: TranzilaSettings[K]) =>
    setSettings(prev => ({ ...prev, [key]: val }));

  const method = settings.default_payment_method;
  const paymentUsed = settings.default_document_type === 'IR' || (settings.credit_enabled && settings.credit_document_type === 'IR');

  return (
    <div className="max-w-5xl mx-auto space-y-6" dir="rtl">
      <SettingsTabs />

      {toast && (
        <div className="flex items-center gap-3 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700">
          {toast}
        </div>
      )}

      <IntegrationSettingsLayout
        providerLabel="טרנזילה (Tranzila)"
        logoText="💳"
        logoBg="bg-teal-50"
        integration={integration}
        loading={loading}
        saving={saving}
        error={error}
        onSave={handleSave}
        onEditCredentials={() => navigate('/app/settings/integrations')}
      >
        <SettingsSection
          title="בדיקת חיבור"
          description="בודק מול טרנזילה ששם המסוף והמפתחות נכונים, בלי ליצור מסמך."
          icon={<PlugZap className="w-4 h-4 text-teal-600" />}
        >
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={testConnection}
              disabled={testing}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-teal-700 border border-teal-200 bg-white hover:bg-teal-50 rounded-lg transition-colors disabled:opacity-60"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlugZap className="w-4 h-4" />}
              {testing ? 'בודק…' : 'בדוק חיבור'}
            </button>
            {testResult && (
              <span className={`flex items-center gap-1.5 text-sm ${testResult.ok ? 'text-emerald-700' : 'text-red-600'}`}>
                {testResult.ok ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
                {testResult.msg}
              </span>
            )}
          </div>
        </SettingsSection>

        <SettingsSection
          title="הפקת חשבונית מתעודת משלוח"
          description="כפתור בתעודת המשלוח שמפיק בטרנזילה מסמך עם כל שורות המוצרים"
          icon={<Send className="w-4 h-4 text-teal-600" />}
        >
          <ToggleRow
            label='הצג כפתור "הפק בטרנזילה"'
            description="הכפתור יופיע בתעודות משלוח למשתמשים עם הרשאת עריכה במלאי."
            checked={settings.manual_push_documents}
            onChange={v => set('manual_push_documents', v)}
          />
          <SelectRow
            label="סוג המסמך שיופק"
            value={settings.default_document_type}
            options={[
              { value: 'IR', label: 'חשבונית מס/קבלה (התשלום כבר התקבל)' },
              { value: 'IN', label: 'חשבונית מס (תשלום בהמשך)' },
              { value: 'DI', label: 'חשבון עסקה' },
            ]}
            onChange={v => set('default_document_type', v as TranzilaSettings['default_document_type'])}
          />
          <SelectRow
            label="תאריך המסמך"
            value={settings.document_date_source}
            options={[
              { value: 'today',    label: 'תאריך ההפקה (היום)' },
              { value: 'document', label: 'תאריך תעודת המשלוח' },
            ]}
            onChange={v => set('document_date_source', v as TranzilaSettings['document_date_source'])}
          />
          <SelectRow
            label='שיעור המע"מ'
            description='לקוחות פטורים ממע"מ תמיד יופקו ללא מע"מ.'
            value={settings.vat_source}
            options={[
              { value: 'system',   label: 'לפי התעודה במערכת' },
              { value: 'terminal', label: 'לפי הגדרת המסוף בטרנזילה' },
            ]}
            onChange={v => set('vat_source', v as TranzilaSettings['vat_source'])}
          />
          <SelectRow
            label="שפת המסמך"
            value={settings.document_language}
            options={[
              { value: 'heb', label: 'עברית' },
              { value: 'eng', label: 'אנגלית' },
            ]}
            onChange={v => set('document_language', v as TranzilaSettings['document_language'])}
          />
          <ToggleRow
            label='הצג מק"ט בשורות המוצרים'
            checked={settings.include_item_code}
            onChange={v => set('include_item_code', v)}
          />
        </SettingsSection>

        <SettingsSection
          title="פרטי הלקוח במסמך"
          description="אילו פרטים מכרטיס הלקוח יופיעו על המסמך בטרנזילה"
          icon={<UserRound className="w-4 h-4 text-sky-600" />}
        >
          <ToggleRow label="שם החברה" checked={settings.send_client_company} onChange={v => set('send_client_company', v)} />
          <ToggleRow label="מספר עוסק / ח.פ." checked={settings.send_client_id} onChange={v => set('send_client_id', v)} />
          <ToggleRow label="כתובת מייל" checked={settings.send_client_email} onChange={v => set('send_client_email', v)} />
          <Field label="שם לקוח כשאין לקוח בתעודה">
            <input type="text" maxLength={60} value={settings.fallback_client_name}
                   onChange={e => set('fallback_client_name', e.target.value)} className={`${inputCls} max-w-sm`} />
          </Field>
        </SettingsSection>

        <SettingsSection
          title="אמצעי תשלום"
          description="נרשם בחשבונית מס/קבלה. סכום התשלום הוא הסכום הכולל של התעודה."
          icon={<Wallet className="w-4 h-4 text-amber-600" />}
        >
          {!paymentUsed && (
            <p className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
              אמצעי התשלום רלוונטי רק כשסוג המסמך הוא חשבונית מס/קבלה.
            </p>
          )}
          <SelectRow
            label="אמצעי תשלום ברירת מחדל"
            value={String(method)}
            options={PAYMENT_METHOD_OPTIONS}
            onChange={v => set('default_payment_method', Number(v))}
          />
          {method === 1 && (
            <>
              <SelectRow
                label="סוג עסקת אשראי"
                value={String(settings.cc_credit_term)}
                options={CREDIT_TERM_OPTIONS}
                onChange={v => set('cc_credit_term', Number(v))}
              />
              {settings.cc_credit_term !== 1 && (
                <NumberRow label="מספר תשלומים" value={settings.cc_installments} min={2} max={36}
                           onChange={v => set('cc_installments', v)} />
              )}
            </>
          )}
          {(method === 3 || method === 4) && (
            <Field label="פרטי הבנק (לא חובה)" hint="ימולאו כברירת מחדל בשורת התשלום.">
              <div className="grid grid-cols-3 gap-2 max-w-md">
                <input inputMode="numeric" placeholder="מס' בנק" maxLength={3} value={settings.bank_code}
                       onChange={e => set('bank_code', e.target.value)} className={inputCls} />
                <input inputMode="numeric" placeholder="סניף" maxLength={5} value={settings.bank_branch}
                       onChange={e => set('bank_branch', e.target.value)} className={inputCls} />
                <input inputMode="numeric" placeholder="מס' חשבון" maxLength={20} value={settings.bank_account}
                       onChange={e => set('bank_account', e.target.value)} className={inputCls} />
              </div>
            </Field>
          )}
          {method === 10 && (
            <Field label="תיאור התשלום" hint="יופיע במסמך בשורת התשלום.">
              <input type="text" maxLength={60} value={settings.other_payment_description}
                     onChange={e => set('other_payment_description', e.target.value)} className={`${inputCls} max-w-sm`} />
            </Field>
          )}
          <ToggleRow
            label="אפשר לשנות את אמצעי התשלום בזמן ההפקה"
            description="בחלון ההפקה יופיעו פרטי התשלום (ממולאים מראש לפי ברירת המחדל) וניתן יהיה לשנות אותם, למשל להזין 4 ספרות של כרטיס או מספר צ'ק."
            checked={settings.ask_payment_each_time}
            onChange={v => set('ask_payment_each_time', v)}
          />
        </SettingsSection>

        <SettingsSection
          title="חשבונית זיכוי בהחזרות"
          description='כפתור "הפק זיכוי בטרנזילה" בתעודות החזרה מלקוח'
          icon={<RotateCcw className="w-4 h-4 text-rose-600" />}
        >
          <ToggleRow
            label="אפשר הפקת חשבונית זיכוי"
            description="הזיכוי יכלול את השורות שהוחזרו בתעודת ההחזרה."
            checked={settings.credit_enabled}
            onChange={v => set('credit_enabled', v)}
          />
          <SelectRow
            label="סוג מסמך הזיכוי"
            value={settings.credit_document_type}
            options={[
              { value: 'IN', label: 'חשבונית מס זיכוי' },
              { value: 'IR', label: 'חשבונית מס/קבלה זיכוי (כולל החזר כספי)' },
            ]}
            onChange={v => set('credit_document_type', v as TranzilaSettings['credit_document_type'])}
            disabled={!settings.credit_enabled}
          />
          <ToggleRow
            label="קשר את הזיכוי לחשבונית המקורית"
            description="אם ההחזרה נוצרה מתעודת משלוח שהופקה לה חשבונית בטרנזילה, מספר החשבונית יופיע על הזיכוי."
            checked={settings.credit_link_original}
            onChange={v => set('credit_link_original', v)}
            disabled={!settings.credit_enabled}
          />
        </SettingsSection>

        <SettingsSection
          title="שליחת המסמך ללקוח במייל"
          description="אחרי ההפקה המערכת תשלח ללקוח מייל עם קישור למסמך בטרנזילה"
          icon={<Mail className="w-4 h-4 text-blue-600" />}
        >
          <ToggleRow
            label="שלח אוטומטית אחרי ההפקה"
            description="נשלח לכתובת המייל שבכרטיס הלקוח. אם אין כתובת, המסמך יופק ותוצג הודעה. תמיד אפשר לשלוח שוב מהמסמך."
            checked={settings.auto_email}
            onChange={v => set('auto_email', v)}
          />
          <Field label="נושא המייל">
            <input type="text" maxLength={150} value={settings.email_subject}
                   onChange={e => set('email_subject', e.target.value)} className={inputCls} />
          </Field>
          <Field label="תוכן המייל" hint="אפשר להשתמש ב: {לקוח} {מספר} {סכום} {סוג}. כפתור לצפייה במסמך יתווסף אוטומטית.">
            <textarea rows={4} maxLength={2000} value={settings.email_body}
                      onChange={e => set('email_body', e.target.value)} className={`${inputCls} resize-y leading-relaxed`} />
          </Field>
          <Field label="שלח העתק אל (לא חובה)">
            <input type="email" dir="ltr" maxLength={120} value={settings.email_cc} placeholder="office@example.com"
                   onChange={e => set('email_cc', e.target.value)} className={`${inputCls} max-w-sm text-left`} />
          </Field>
        </SettingsSection>

        <div className="flex items-start gap-3 px-4 py-3 bg-teal-50 border border-teal-100 rounded-xl text-xs text-teal-800">
          <Zap className="w-3.5 h-3.5 shrink-0 mt-0.5 text-teal-500" />
          <p>
            <strong>שים לב:</strong> {'נדרש שירות החשבוניות של טרנזילה. כל תעודה ניתן להפיק פעם אחת בלבד, ומספר המסמך בטרנזילה נשמר עליה.'}
          </p>
        </div>

        <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border border-gray-100 rounded-xl">
          <div>
            <p className="text-sm font-medium text-gray-700">תיעוד ממשק החשבוניות של טרנזילה</p>
            <p className="text-xs text-gray-400 mt-0.5">מדריך טכני להפקת מסמכים</p>
          </div>
          <a
            href="https://docs.tranzila.com/docs/invoices/invoices-api"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-teal-700 border border-teal-200 bg-white rounded-lg hover:bg-teal-50 transition-colors"
          >
            <FileText className="w-3.5 h-3.5" />
            פתח תיעוד
          </a>
        </div>
      </IntegrationSettingsLayout>
    </div>
  );
}
